import {
  ListenableSubscribable,
  SubscribableReconnectingSocket,
  SubscribableReconnectingSocketEvent,
  Subscription,
} from '@benzinga/subscribable';
import { addParamsToURL, Session, AuthenticationManager } from '@benzinga/session';

import { NotificationEnvironment } from './environment';
import { SafeError } from '@benzinga/safe-await';
import { Notification } from './entities';

export interface NotificationsSocketErrorEvent {
  error: SafeError;
  errorType: 'notifications:notifications_socket_requires_you_to_be_logged_in';
  type: 'error';
}

interface NotificationsSessionOpen {
  type: 'notifications:open';
}

interface NotificationsSessionClose {
  type: 'notifications:close';
}
interface NotificationsReconnect {
  type: 'notifications:reconnected' | 'notifications:disconnected';
}

interface NotificationSocketSubscriptionEvent {
  type: 'notifications:subscribe';
}

export interface NotificationReceivedEvent {
  type: 'notifications:notification_received';
  notification: Notification;
}

export type NotificationSocketEvent =
  | NotificationReceivedEvent
  | NotificationsSocketErrorEvent
  | NotificationSocketSubscriptionEvent
  | ErrorEvent
  | NotificationsSessionOpen
  | NotificationsSessionClose
  | NotificationsReconnect;

export class NotificationSocket extends ListenableSubscribable<NotificationSocketEvent> {
  private socket?: SubscribableReconnectingSocket<string>;
  private socketSubscription?: Subscription<SubscribableReconnectingSocket<string>>;
  private session: Session;
  private authSubscription?: Subscription<AuthenticationManager>;

  constructor(session: Session) {
    super();
    this.session = session;
  }

  public close = () => this.socket?.close();

  public reconnect = () => this.socket?.reconnect();

  public createSocket = (token: string) => {
    this.socket = new SubscribableReconnectingSocket(this.getSocketUrl(this.session, token));
    this.socketSubscription = this.socket.subscribe(this.onMessage);
    this.socket.open();
  };

  public clear = (): void => {
    this.socket?.close();
    this.socketSubscription?.unsubscribe();
    this.socketSubscription = undefined;
    this.authSubscription?.unsubscribe();
    this.authSubscription = undefined;
  };

  private getSocketUrl = (session: Session, token: string) => {
    const socketUrl = new URL(session.getEnvironment(NotificationEnvironment).socketUrl);
    return addParamsToURL(socketUrl, { token, type: 'sessionid' });
  };

  private onMessage = (event: SubscribableReconnectingSocketEvent<string>): void => {
    switch (event.type) {
      case 'response': {
        const notification: Notification = JSON.parse(event.msg);
        this.dispatch({
          notification,
          type: 'notifications:notification_received',
        });
        break;
      }
      case 'reconnected':
        this.dispatch({ type: 'notifications:reconnected' });
        break;
      case 'disconnected':
      case 'close':
        this.dispatch({ type: 'notifications:disconnected' });
    }
  };
}
